class InsidePlannersGallery extends HTMLElement {
  constructor() {
    super();
    this.activePart = '1';
    this._scrollListeners = [];
  }

  connectedCallback() {
    this.tabs = Array.from(this.querySelectorAll('[data-cm-ipg-tab]'));
    this.panels = Array.from(this.querySelectorAll('[data-cm-ipg-panel]'));
    this.prevBtn = this.querySelector('[data-cm-ipg-prev]');
    this.nextBtn = this.querySelector('[data-cm-ipg-next]');

    this.tabs.forEach((tab) => {
      tab.addEventListener('click', () => this.activatePart(tab.dataset.cmIpgTab));
      tab.addEventListener('keydown', (event) => this.onTabKeydown(event));
    });

    if (this.prevBtn) {
      this.prevBtn.addEventListener('click', () => this.advance(-1));
    }
    if (this.nextBtn) {
      this.nextBtn.addEventListener('click', () => this.advance(1));
    }

    this.panels.forEach((panel) => {
      const scroller = panel.querySelector('[data-cm-ipg-scroller]');
      if (scroller) {
        const handler = () => this.updateArrows();
        scroller.addEventListener('scroll', handler, { passive: true });
        this._scrollListeners.push({ scroller, handler });

        // Lazy-loaded slide images may resolve after first activatePart() runs;
        // re-measure once each one has settled so arrows enable correctly.
        scroller.querySelectorAll('img').forEach((img) => {
          if (img.complete) return;
          img.addEventListener('load', () => this.updateArrows(), { once: true });
        });
      }
    });

    window.addEventListener('resize', () => this.updateArrows(), { passive: true });

    this.activatePart(this.dataset.activePart || '1');
  }

  disconnectedCallback() {
    this._scrollListeners.forEach(({ scroller, handler }) => {
      scroller.removeEventListener('scroll', handler);
    });
    this._scrollListeners = [];
  }

  onTabKeydown(event) {
    const currentIndex = this.tabs.indexOf(event.currentTarget);
    if (currentIndex === -1) return;

    let nextIndex = null;
    if (event.key === 'ArrowRight') nextIndex = (currentIndex + 1) % this.tabs.length;
    if (event.key === 'ArrowLeft') nextIndex = (currentIndex - 1 + this.tabs.length) % this.tabs.length;
    if (event.key === 'Home') nextIndex = 0;
    if (event.key === 'End') nextIndex = this.tabs.length - 1;

    if (nextIndex !== null) {
      event.preventDefault();
      const nextTab = this.tabs[nextIndex];
      this.activatePart(nextTab.dataset.cmIpgTab);
      nextTab.focus();
    }
  }

  activatePart(part) {
    if (!part) return;
    this.activePart = String(part);
    this.dataset.activePart = this.activePart;

    this.tabs.forEach((tab) => {
      const isActive = tab.dataset.cmIpgTab === this.activePart;
      tab.setAttribute('aria-selected', isActive ? 'true' : 'false');
      tab.tabIndex = isActive ? 0 : -1;
    });

    this.panels.forEach((panel) => {
      const isActive = panel.dataset.cmIpgPanel === this.activePart;
      if (isActive) {
        panel.removeAttribute('hidden');
      } else {
        panel.setAttribute('hidden', '');
      }
    });

    // Defer until after layout so scrollLeft/scrollWidth are accurate
    requestAnimationFrame(() => this.updateArrows());
  }

  activeScroller() {
    const activePanel = this.querySelector(`[data-cm-ipg-panel="${this.activePart}"]`);
    if (!activePanel) return null;
    return activePanel.querySelector('[data-cm-ipg-scroller]');
  }

  advance(direction) {
    const scroller = this.activeScroller();
    if (!scroller) return;

    const slides = scroller.querySelectorAll('[data-cm-ipg-slide]');
    if (slides.length === 0) return;

    const firstSlide = slides[0];
    const slideWidth = firstSlide.getBoundingClientRect().width;
    const styles = window.getComputedStyle(scroller);
    const gap = parseFloat(styles.columnGap || styles.gap || '0') || 0;

    scroller.scrollBy({
      left: direction * (slideWidth + gap),
      behavior: 'smooth',
    });
  }

  updateArrows() {
    const scroller = this.activeScroller();
    if (!scroller || !this.prevBtn || !this.nextBtn) return;

    const atStart = scroller.scrollLeft <= 4;
    const atEnd = scroller.scrollLeft + scroller.clientWidth >= scroller.scrollWidth - 4;

    this.prevBtn.disabled = atStart;
    this.nextBtn.disabled = atEnd;
  }
}

if (!customElements.get('inside-planners-gallery')) {
  customElements.define('inside-planners-gallery', InsidePlannersGallery);
}
